/*!
 * PatternFly Elements: PfeProgressSteps 1.12.2
 * @license
 * Copyright 2021 Red Hat, Inc.
 * 
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 * 
*/

// Import polyfills: findIndex
import "./polyfills--pfe-progress-steps.js";
import PFElement from "../../pfelement/dist/pfelement.js";
import "./pfe-progress-steps-item.js";
class PfeProgressSteps extends PFElement {

  // Injected at build-time
  static get version() {
    return "1.12.2";
  }

  // Injected at build-time
  get html() {
    return `
<style>:host{display:block;display:-webkit-box;display:-webkit-flex;display:-ms-flexbox;display:flex;width:auto;-webkit-box-pack:justify;-webkit-justify-content:space-between;-ms-flex-pack:justify;justify-content:space-between;position:relative;-webkit-box-align:start;-webkit-align-items:flex-start;-ms-flex-align:start;align-items:flex-start}@media screen and (-ms-high-contrast:active),screen and (-ms-high-contrast:none){:host{display:none}}:host([vertical]){-webkit-box-orient:vertical;-webkit-box-direction:normal;-webkit-flex-direction:column;-ms-flex-direction:column;flex-direction:column}:host([hidden]){display:none}.pfe-progress-steps__progress-bar{display:-webkit-box;display:-webkit-flex;display:-ms-flexbox;display:flex;position:absolute;top:calc(32px - 20px + 2px);top:calc(var(--pfe-progress-steps-item__circle--size--active,var(--pfe-theme--ui--element--size--md,32px)) - var(--pfe-progress-steps-item__circle--size,var(--pfe-theme--ui--element--size,20px)) + var(--pfe-progress-steps__progress-bar--thickness,var(--pfe-theme--ui--border-width--md,2px)));left:calc(75px / 2);left:calc(var(--pfe-progress-steps__item--size--first,75px)/ 2);height:2px;height:var(--pfe-progress-steps__progress-bar--thickness,var(--pfe-theme--ui--border-width--md,2px));width:calc(100% - 75px / 2 - 75px / 2);width:calc(100% - var(--pfe-progress-steps__item--size--first,75px)/ 2 - var(--pfe-progress-steps__item--size--last,75px)/ 2);background-color:#d2d2d2;background-color:var(--pfe-progress-steps__progress-bar--color,var(--pfe-theme--color--ui--border--lighter,#d2d2d2))}:host([vertical]) .pfe-progress-steps__progress-bar{width:2px;width:var(--pfe-progress-steps__progress-bar--thickness,var(--pfe-theme--ui--border-width--md,2px));top:calc(32px / 2);top:calc(var(--pfe-progress-steps-item__circle--size--active,var(--pfe-theme--ui--element--size--md,32px))/ 2);left:calc((20px / 2) + 2px);left:calc((var(--pfe-progress-steps-item__circle--size,var(--pfe-theme--ui--element--size,20px))/ 2) + var(--pfe-progress-steps__progress-bar--thickness,var(--pfe-theme--ui--border-width--md,2px)));height:calc(100% - 32px - 1em);height:calc(100% - var(--pfe-progress-steps-item__circle--size--active,var(--pfe-theme--ui--element--size--md,32px)) - 1em)}.pfe-progress-steps__progress-bar--fill{display:block;height:2px;height:var(--pfe-progress-steps__progress-bar--thickness,var(--pfe-theme--ui--border-width--md,2px));background-color:#06c;background-color:var(--pfe-progress-steps__progress-bar--color--active,var(--pfe-theme--color--ui-accent,#06c));width:0}:host([vertical]) .pfe-progress-steps__progress-bar--fill{width:2px;width:var(--pfe-progress-steps__progress-bar--thickness,var(--pfe-theme--ui--border-width--md,2px));height:0} /*# sourceMappingURL=pfe-progress-steps.min.css.map */</style>
<div class="pfe-progress-steps__progress-bar">
    <span class="pfe-progress-steps__progress-bar--fill"></span>
</div>
<slot></slot>`;
  }

  static get tag() {
    return "pfe-progress-steps";
  }

  static get meta() {
    return {
      title: "Progress stepper",
      description:
        "A component that gives the user a visual representation of the current state of their progress through an application (typically a multistep form).",
    };
  }

  get templateUrl() {
    return "pfe-progress-steps.html";
  }

  get styleUrl() {
    return "pfe-progress-steps.scss";
  }

  // Declare the type of this component
  static get PfeType() {
    return PFElement.PfeTypes.Content;
  }

  get stepItems() {
    return [...this.querySelectorAll("pfe-progress-steps-item")];
  }

  get _progressBar() {
    return this.shadowRoot.querySelector(`.${this.tag}__progress-bar--fill`);
  }

  static get properties() {
    return {
      vertical: {
        type: Boolean,
        default: false,
        cascade: ["pfe-progress-steps-item"],
      },
      variant: {
        type: String,
        values: ["count"],
        cascade: ["pfe-progress-steps-item"],
      },
    };
  }

  constructor() {
    super(PfeProgressSteps, { type: PfeProgressSteps.PfeType });
    if (!this.isIE11) this._resizeObserver = new ResizeObserver(this._build.bind(this));
  }

  connectedCallback() {
    super.connectedCallback();
    if (this._resizeObserver) {
      // this will call _build initially and estabilish a resize observer for this element
      this._resizeObserver.observe(this);
    } else {
      // if we don't have access to resize observer then
      // manually call build.
      this._build();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._resizeObserver) this._resizeObserver.disconnect();
  }

  _build() {
    if (this.isIE11) return;

    const items = this.stepItems;

    // find what child item has the active state
    const activeItemIndex = items.findIndex((element) => element.current);
    if (activeItemIndex >= 0) {
      // Calculate the size of the progress bar.
      const size = (activeItemIndex / (items.length - 1)) * 100 + "%";
      const dimension = this.vertical ? "height" : "width";
      this._progressBar.style[dimension] = size;
    }

    for (let index = 0; index < items.length; index++) {
      const item = items[index];

      // Set the count on the children
      if (this.variant === "count") item.setAttribute("count", index + 1);

      if (!this.vertical) {
        Promise.all([customElements.whenDefined(item.tagName.toLowerCase())]).then(() => {
          if (index === 0) {
            this.style.setProperty(
              `--${this.tag}__item--size--first`,
              `${parseInt(item.getBoundingClientRect().width)}px`
            );
          } else if (index === items.length - 1) {
            this.style.setProperty(
              `--${this.tag}__item--size--last`,
              `${parseInt(item.getBoundingClientRect().width)}px`
            );
          }
        });
      }

      // Add spacing to the each of the items except for the top item
      // @todo we have to do it in javascript until everyone supports
      // targeting siblings in :slotted. i.e. slot:slotted(pfe-progress-steps-item + pfe-progress-steps-item) { margin-top }
      else {
        // if it's the last item then we'll explicitly set the min-height
        // to 0 so the circle and lines stop at the top of the last item.
        if (index === items.length - 1) item.style.minHeight = "0";
        // if it's not the last item then unset any inline min-height style
        // that was set.
        else item.style.minHeight = "";
      }
    }
  }
}

PFElement.create(PfeProgressSteps);

export default PfeProgressSteps;
